"use client";

import { useState, useEffect } from "react";
import { useSwipe } from "@/hooks/useSwipe";
import ImagePreview from "@/components/ImagePreview";
import clsx from "clsx";

interface ImageListProps {
    images: string[];
    title?: string;
    ratio?: number;
}

export default function ImageList({ images, title, ratio = 16 / 9 }: ImageListProps) {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [containerHeight, setContainerHeight] = useState('100%');

    // 以第一张图片作为基础高度，重新计算容器高度
    useEffect(() => {
        setContainerHeight(`${Math.min(100 / ratio, 150)}vw`);
    }, [ratio]);

    // 图片切换
    const handlePrevImage = () => {
        setCurrentImageIndex((prev) => Math.max(0, prev - 1));
    };

    const handleNextImage = () => {
        setCurrentImageIndex((prev) => Math.min(images.length - 1, prev + 1));
    };

    // 滑动处理
    const { handlers: swipeHandlers } = useSwipe({
        onSwipeLeft: () => {
            if (currentImageIndex < images.length - 1) {
                handleNextImage();
            }
        },
        onSwipeRight: () => {
            if (currentImageIndex > 0) {
                handlePrevImage();
            }
        },
        threshold: 50,
    });

    if (!images || images.length === 0) return null;

    return (
        <div>
            {/* 图片区域 */}
            <div className="relative" {...swipeHandlers}>
                <div className="bg-white w-full">
                    <div
                        className="w-screen flex overflow-hidden"
                        style={{
                            minHeight: "200px",
                            maxHeight: "150vw",
                            height: `${containerHeight}`,
                        }}
                    >
                        {images.map((image, index) => (
                            <div
                                key={`${image}-${index}`}
                                className="min-w-full max-w-full transition-transform duration-300 flex items-center justify-center"
                                style={{
                                    transform: `translateX(${-currentImageIndex * 100}%)`,
                                }}
                            >
                                <img
                                    onClick={() => setPreviewOpen(true)}
                                    src={image}
                                    alt={title}
                                    className="object-contain transition-transform duration-300 cursor-zoom-in"
                                    style={{
                                        maxHeight: `${containerHeight}`,
                                    }}
                                    onLoad={(e) => {
                                        const img = e.target as HTMLImageElement;
                                        if (img.naturalWidth > img.naturalHeight) {
                                            img.style.width = "100%";
                                            img.style.height = "auto";
                                        } else {
                                            img.style.width = "100%";
                                            img.style.height = `${containerHeight}`;
                                        }
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* 图片指示器 */}
                {images.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-sm px-2 py-1 rounded-full">
                        {currentImageIndex + 1}/{images.length}
                    </div>
                )}
            </div>

            {/* 底部点指示器 */}
            {images.length > 1 && (
                <div className="w-full h-4 flex justify-center space-x-2 items-center mt-1">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={clsx(
                                "w-2 h-2 rounded-full transition-all",
                                index === currentImageIndex ? `bg-primary` : "bg-black bg-opacity-50"
                            )}
                        />
                    ))}
                </div>
            )}

            {/* 图片预览 */}
            <ImagePreview
                isMobile={true}
                src={images[currentImageIndex]}
                isOpen={previewOpen}
                onOpenChange={setPreviewOpen}
            />
        </div>
    );
}